import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Package, Truck, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'APPROVED', label: 'Approved', icon: CheckCircle, color: 'green' },
  { value: 'IN_PRODUCTION', label: 'In Production', icon: Package, color: 'blue' },
  { value: 'SHIPPED', label: 'Shipped', icon: Truck, color: 'purple' },
  { value: 'DELIVERED', label: 'Delivered', icon: Home, color: 'teal' }
];

export const RelicClaimRequestsManager = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 50,
    total_pages: 0
  });

  const extractProofUrl = (message) => {
    if (!message) return null;
    const match = message.match(/PROOF OF PURCHASE:\n(https?:\/\/[^\n]+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    fetchClaims();
  }, [statusFilter]);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'ALL') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/relic/claims?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClaims(response.data.claims);
      setPagination({
        total: response.data.total,
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (claimId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API_URL}/api/admin/relic/claims/${claimId}/update-status`,
        {
          status: newStatus,
          admin_notes: adminNotes,
          tracking_number: trackingNumber
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowStatusModal(false);
      setSelectedClaim(null);
      setAdminNotes('');
      setTrackingNumber('');
      fetchClaims();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleReject = async (claimId) => {
    if (!window.confirm('Reject this claim? The code will be reset to UNUSED.')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API_URL}/api/admin/relic/claims/${claimId}/reject`,
        { admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSelectedClaim(null);
      setAdminNotes('');
      fetchClaims();
      alert('Claim rejected successfully!');
    } catch (error) {
      console.error('Error rejecting claim:', error);
      alert('Failed to reject claim');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      IN_PRODUCTION: { color: 'bg-blue-100 text-blue-800', icon: Package },
      SHIPPED: { color: 'bg-purple-100 text-purple-800', icon: Truck },
      DELIVERED: { color: 'bg-teal-100 text-teal-800', icon: Home },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const badge = badges[status] || badges.PENDING;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs rounded-full ${badge.color}`}>
        <Icon className="w-3 h-3" />
        <span>{status.replace('_', ' ')}</span>
      </span>
    );
  };

  const getRarityBadge = (rarity) => {
    const colors = {
      COMMON: 'bg-gray-100 text-gray-700',
      UNCOMMON: 'bg-green-100 text-green-700',
      RARE: 'bg-blue-100 text-blue-700',
      LEGENDARY: 'bg-purple-100 text-purple-700'
    };
    return colors[rarity] || colors.COMMON;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Claim Requests</h1>
        
        <div className="flex flex-wrap gap-2">
          {['ALL', 'PENDING', 'APPROVED', 'IN_PRODUCTION', 'SHIPPED', 'DELIVERED', 'REJECTED'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                statusFilter === status
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : claims.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No {statusFilter !== 'ALL' ? statusFilter.toLowerCase() : ''} claims found
          </div>
        ) : (
          <div className="divide-y">
            {claims.map((claim) => (
              <div key={claim.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-lg font-mono font-bold">{claim.code}</span>
                      {claim.rarity && (
                        <span className={`px-2 py-1 text-xs rounded-full ${getRarityBadge(claim.rarity)}`}>
                          {claim.rarity}
                        </span>
                      )}
                      {getStatusBadge(claim.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-gray-600">Name</div>
                        <div className="font-medium">{claim.name}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Email</div>
                        <div className="font-medium">{claim.email}</div>
                      </div>
                      <div>
                        <div className="text-gray-600">Purchase Date</div>
                        <div className="font-medium">
                          {new Date(claim.purchase_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-600">Submitted</div>
                        <div className="font-medium">
                          {new Date(claim.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    
                    {claim.message && (
                      <div className="mt-3 text-sm">
                        <div className="text-gray-600">Message</div>
                        <div className="text-gray-900 bg-gray-50 p-2 rounded mt-1">
                          {claim.message}
                        </div>
                      </div>
                    )}
                    
                    {claim.tracking_number && (
                      <div className="mt-3 text-sm">
                        <div className="text-gray-600">Tracking Number</div>
                        <div className="font-mono font-medium">{claim.tracking_number}</div>
                      </div>
                    )}
                    
                    {claim.batch_name && (
                      <div className="mt-2 text-sm text-gray-600">
                        Batch: <span className="font-medium">{claim.batch_name}</span>
                      </div>
                    )}
                    
                    {claim.admin_notes && (
                      <div className="mt-3 text-sm">
                        <div className="text-gray-600">Admin Notes</div>
                        <div className="text-gray-900 bg-blue-50 p-2 rounded mt-1">
                          {claim.admin_notes}
                        </div>
                      </div>
                    )}
                    
                    {extractProofUrl(claim.message) && (
                      <div className="mt-3">
                        <div className="text-gray-600 text-sm mb-1">Proof of Purchase</div>
                        <img
                          src={extractProofUrl(claim.message)}
                          alt="Proof of purchase"
                          className="w-24 h-24 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedImage(extractProofUrl(claim.message));
                            setShowImageModal(true);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col space-y-2 ml-4">
                    {claim.status === 'PENDING' ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedClaim(claim);
                            setAdminNotes('');
                            setTrackingNumber('');
                            setShowStatusModal(true);
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm whitespace-nowrap"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Approve</span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedClaim(claim);
                            setAdminNotes('');
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm whitespace-nowrap"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Reject</span>
                        </button>
                      </>
                    ) : claim.status !== 'REJECTED' && claim.status !== 'DELIVERED' ? (
                      <button
                        onClick={() => {
                          setSelectedClaim(claim);
                          setAdminNotes(claim.admin_notes || '');
                          setTrackingNumber(claim.tracking_number || '');
                          setShowStatusModal(true);
                        }}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm whitespace-nowrap"
                      >
                        Update Status
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {showStatusModal && selectedClaim && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {selectedClaim.status === 'PENDING' ? 'Approve Claim' : 'Update Status'}
            </h2>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-mono font-bold">{selectedClaim.code}</div>
                <div className="text-sm text-gray-600">{selectedClaim.name} - {selectedClaim.email}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">
                  {selectedClaim.status === 'PENDING' ? 'Approve and set status to:' : 'New Status'}
                </label>
                <div className="space-y-2">
                  {STATUS_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    // Only show appropriate next statuses
                    const canShow = selectedClaim.status === 'PENDING' || 
                      (selectedClaim.status === 'APPROVED' && option.value !== 'APPROVED') ||
                      (selectedClaim.status === 'IN_PRODUCTION' && ['SHIPPED', 'DELIVERED'].includes(option.value)) ||
                      (selectedClaim.status === 'SHIPPED' && option.value === 'DELIVERED');
                    
                    if (!canShow) return null;
                    
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleUpdateStatus(selectedClaim.id, option.value)}
                        className="w-full flex items-center space-x-2 px-4 py-3 border-2 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                      >
                        <Icon className="w-5 h-5 text-gray-700" />
                        <span className="font-medium">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {(selectedClaim.status === 'IN_PRODUCTION' || selectedClaim.status === 'SHIPPED') && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tracking Number</label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Optional tracking number"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Admin Notes (optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this claim..."
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex space-x-3 pt-4 mt-4 border-t">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedClaim(null);
                  setAdminNotes('');
                  setTrackingNumber('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {selectedClaim && !showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Reject Claim</h2>
            
            <div className="space-y-3 mb-4">
              <div className="text-center">
                <div className="text-2xl font-mono font-bold">{selectedClaim.code}</div>
                <div className="text-sm text-gray-600">{selectedClaim.name} - {selectedClaim.email}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Rejection Reason</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Why is this claim being rejected?"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setSelectedClaim(null);
                  setAdminNotes('');
                }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleReject(selectedClaim.id)}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Reject Claim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl"
            >
              ✕
            </button>
            <img
              src={selectedImage}
              alt="Proof of purchase - full size"
              className="max-w-full max-h-[90vh] object-contain rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};
